<script setup lang="ts">
import type { DateValue } from 'reka-ui';
import {
  CalendarCell,
  CalendarCellTrigger,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHead,
  CalendarGridRow,
  CalendarHeadCell,
  CalendarHeader,
  CalendarNext,
  CalendarPrev,
} from 'reka-ui';
import { ChevronLeft, ChevronRight } from 'lucide-vue-next';
import { DateFormatter, getLocalTimeZone } from '@internationalized/date';

defineProps<{
  month: { value: DateValue; rows: DateValue[][] };
  weekDays: string[];
  vertical?: boolean;
  hideNav?: boolean;
  isFirst?: boolean;
  isLast?: boolean;
  directFlightDates?: string[];
}>();

const { t } = useI18n();
const fullLocale = useFullLocale();
const df = computed(() => new DateFormatter(fullLocale.value, { month: 'long', year: 'numeric' }));
const directFlightHintId = useId();
</script>

<template>
  <div
    :class="cn('flex flex-col gap-3', !vertical && 'p-3 md:px-6 md:py-5', vertical && 'w-fit')"
    :data-calendar-month="`${month.value.year}-${month.value.month}`"
  >
    <CalendarHeader class="flex items-center">
      <CalendarPrev
        v-if="isFirst && !hideNav"
        :aria-label="t('components.common.previousMonth')"
        class="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-transparent hover:bg-gray-50 disabled:pointer-events-none disabled:opacity-50"
      >
        <ChevronLeft
          class="h-5 w-5 text-gray-500"
          aria-hidden="true"
        />
      </CalendarPrev>

      <span
        :class="cn('flex-1 text-center text-base capitalize md:text-sm', vertical ? 'font-semibold' : 'font-medium')"
      >
        {{ df.format(month.value.toDate(getLocalTimeZone())) }}
      </span>

      <CalendarNext
        v-if="isLast && !hideNav"
        :aria-label="t('components.common.nextMonth')"
        class="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-transparent hover:bg-gray-50 disabled:pointer-events-none disabled:opacity-50"
      >
        <ChevronRight
          class="h-5 w-5 text-gray-500"
          aria-hidden="true"
        />
      </CalendarNext>
    </CalendarHeader>

    <CalendarGrid>
      <CalendarGridHead>
        <CalendarGridRow class="flex">
          <CalendarHeadCell
            v-for="day in weekDays"
            :key="day"
            class="w-11 text-center text-base font-medium text-gray-700 lg:w-10 lg:text-sm"
          >
            {{ day }}
          </CalendarHeadCell>
        </CalendarGridRow>
      </CalendarGridHead>
      <CalendarGridBody class="cursor-pointer">
        <CalendarGridRow
          v-for="(week, i) in month.rows"
          :key="i"
          class="mt-1 flex"
        >
          <CalendarCell
            v-for="date in week"
            :key="date.toString()"
            :date="date"
            :aria-hidden="date.month !== month.value.month || date.year !== month.value.year ? 'true' : undefined"
            class="relative p-0 text-center text-base focus-within:relative focus-within:z-20 lg:text-sm [&:has([data-disabled])]:cursor-default"
          >
            <CalendarCellTrigger
              :day="date"
              :month="month.value"
              class="grid h-11 w-11 grid-rows-3 place-items-center rounded-full p-0 text-base font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-100 focus-visible:outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[outside-view]:opacity-0 lg:h-10 lg:w-10 lg:text-sm [&[data-selected]]:bg-brand-primary-600 [&[data-selected]]:text-white [&[data-selected]]:hover:bg-brand-primary-600"
              :aria-describedby="directFlightDates?.includes(date.toString()) ? directFlightHintId : undefined"
            >
              <template #default="{ dayValue }">
                <span class="row-start-2">{{ dayValue }}</span>
                <UiCustomRangeCalendarDirectFlightDot
                  v-if="directFlightDates?.includes(date.toString())"
                  class="row-start-3 [[data-selected]_&]:bg-white"
                />
              </template>
            </CalendarCellTrigger>
          </CalendarCell>
        </CalendarGridRow>
      </CalendarGridBody>
    </CalendarGrid>
    <!-- reka's aria-label replaces the cell content for a screen reader: the dot is told here -->
    <span
      :id="directFlightHintId"
      class="sr-only"
    >
      {{ t('components.search.datepicker.directFlightLegend') }}
    </span>
  </div>
</template>
